import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { IconButton } from '../../../packages/ui/molecules/icon-button';
import { Input } from '../../../packages/ui/atoms/input';
import { Icons } from '../../../packages/icons';
import { fontWeight, Text, textLevel } from '../../../packages/ui/atoms/typography';
import { useStyles, sxStyles } from './AskMeAnythingSession.styles';
import { colors } from '../../../packages';

const AskMeAnythingSession = ({
  isError,
  isLoading,
  question,
  setQuestion,
  onSubmit,
  onCopilotAction,
  selectedCopilotOption,
  setSelectedCopilotOption,
  conversationHistory = [],
}) => {
  const classes = useStyles();
  const conversationEndRef = useRef(null);

  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationHistory, isLoading]);

  const handleCopilotClick = action => {
    if (isLoading) return;

    if (setSelectedCopilotOption) {
      setSelectedCopilotOption(selectedCopilotOption === action ? null : action);
    }
    if (onCopilotAction) {
      onCopilotAction(action);
    }
  };

  const getCopilotButtonStyle = action => ({
    ...sxStyles.optionButton,
    ...(selectedCopilotOption === action ? sxStyles.optionButtonSelected : {}),
    ...(isLoading ? sxStyles.optionButtonDisabled : {}),
  });

  return (
    <div className={classes.wrapper}>
      <div className={classes.container}>
        <Box sx={sxStyles.conversationContainer}>
          <Box sx={sxStyles.conversationHistory}>
            {conversationHistory.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                gap="8px"
                flex={1}
                justifyContent="center"
                alignItems="center"
              >
                <Text weight={fontWeight.BOLD} level={textLevel.L} color={colors.neutral900}>
                  How can I help with your clinical work today?
                </Text>
                <Text weight={fontWeight.MEDIUM} level={textLevel.M} color={colors.neutral500}>
                  Get personalized support with notes, diagnoses, interventions, and more.
                </Text>
              </Box>
            ) : (
              conversationHistory.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    ...sxStyles.messageContainer,
                    ...(message.type === 'sent' ? sxStyles.sentMessage : sxStyles.receivedMessage),
                  }}
                >
                  {message.type === 'sent' ? (
                    <Text weight={fontWeight.MEDIUM} size={textLevel.XS}>
                      {message.content}
                    </Text>
                  ) : (
                    <p dangerouslySetInnerHTML={{ __html: message.content }}></p>
                  )}
                </Box>
              ))
            )}
            {isError && !isLoading && (
              <Box className={classes.error}>
                <Text className={classes.errorHeading} weight={fontWeight.SEMI_BOLD}>
                  An error occurred while processing your question.
                </Text>
              </Box>
            )}
            {isLoading && (
              <Box className={classes.loaderContainer}>
                <Text className={classes.loaderText} weight={fontWeight.SEMI_BOLD}>
                  Give us a few moments, we're working our magic
                </Text>
                <Icons className={classes.loaderIcon} glyph="magic-wand" />
              </Box>
            )}
            <div ref={conversationEndRef} />
          </Box>
        </Box>
      </div>
      <Box className={classes.footer}>
        <div className={classes.inputContainer}>
          <Input
            value={question}
            className={classes.input}
            variant="DEFAULT"
            placeholder="Ask anything"
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={event => {
              if (event.key === ' ') {
                event.stopPropagation();
              }
              if (event.key === 'Enter' && !isLoading && question.trim()) {
                event.preventDefault();
                onSubmit();
              }
            }}
          />
          <IconButton
            onClick={onSubmit}
            variant="OUTLINE"
            className={classes.button}
            disabled={isLoading || !question}
          >
            <Icons glyph="arrow-right" />
          </IconButton>
        </div>
        <Box sx={sxStyles.copilotSection}>
          <Box sx={sxStyles.copilotOptions}>
            <Box
              sx={getCopilotButtonStyle('patient-case-conceptualizer')}
              onClick={() => handleCopilotClick('patient-case-conceptualizer')}
            >
              <Icons glyph="lottie-questionaire" />
              <Text
                color={`${
                  selectedCopilotOption === 'patient-case-conceptualizer' ? colors.white : colors.neutral900
                }`}
                weight={fontWeight.MEDIUM}
                size={textLevel.XL}
              >
                Patient Case Conceptualizer
              </Text>
            </Box>
            <Box
              sx={getCopilotButtonStyle('patient-engagement-insights')}
              onClick={() => handleCopilotClick('patient-engagement-insights')}
            >
              <Icons glyph="lottie-care-team" />
              <Text
                color={`${
                  selectedCopilotOption === 'patient-engagement-insights' ? colors.white : colors.neutral900
                }`}
                weight={fontWeight.MEDIUM}
                size={textLevel.M}
              >
                Patient Engagement Insights
              </Text>
            </Box>
            <Box
              sx={getCopilotButtonStyle('milestone-celebrator')}
              onClick={() => handleCopilotClick('milestone-celebrator')}
            >
              <Icons glyph="lottie-questionaire" />
              <Text
                color={`${
                  selectedCopilotOption === 'milestone-celebrator' ? colors.white : colors.neutral900
                }`}
                weight={fontWeight.MEDIUM}
                size={textLevel.M}
              >
                Milestone Celebrator
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default AskMeAnythingSession;
